// pages/Connector/GoogleDrive/GoogleDriveConnector.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from 'react-router-dom';
import backendURL from "../../../config";
import ListGoogleDriveFiles from "./ListGoogleDriveFiles";
import useDrivePicker from 'react-google-drive-picker';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function GoogleDriveConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // For overall page load
    const [isConnecting, setIsConnecting] = useState(false); // For "Connect" button action
    const [error, setError] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();

    const [openPicker, authResponse] = useDrivePicker();

    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError('');
        const token = localStorage.getItem("access_token");
        try {
            const response = await axios.get(`${backendURL}/api/google-drive/status`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000 // Timeout after 30 seconds
            });
            setIsConnected(response.data.connected);
            setAccessToken(response.data.accessToken || null);
            if (!response.data.connected) {
                const params = new URLSearchParams(location.search);
                if (params.has('error') || params.has('connected')) {
                    navigate(location.pathname, { replace: true });
                }
            }
        } catch (err) {
            console.error("Error checking Google Drive status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
            setAccessToken(null);
        } finally {
            setIsLoading(false); // Always reset isLoading, regardless of showLoading
        }
    }, [location.pathname, location.search, navigate]);


    useEffect(() => {
        document.title = 'Google Drive Connector | KlonIT';
        const params = new URLSearchParams(location.search);
        if (params.has('connected') && params.get('connected') === 'true') {
            console.log("Successfully connected to Google Drive via redirect.");
            navigate(location.pathname, { replace: true });
            checkStatus(false); // Check status without full page load indicator
            // Potentially trigger a refresh of the data table here if needed
            setRefreshTrigger(prev => prev + 1);
        } else if (params.has('error')) {
             setError(`Authentication failed after redirect: ${params.get('error')}`);
             navigate(location.pathname, { replace: true });
        } else {
            checkStatus(); // Initial status check
        }
    }, []); // Only run once on mount, redirect parameters will trigger re-render & effect below if URL changes

    useEffect(() => {
        // This effect can run if the URL search params change due to redirect from OAuth
        // or if checkStatus is called and updates dependencies
        const params = new URLSearchParams(location.search);
        if (params.has('connected') || params.has('error')) {
            // Already handled by the initial useEffect, or will be if the component remounts.
            // For simple query param changes, might need to re-trigger checkStatus
            // if initial `useEffect` doesn't cover all cases due to its empty dependency array.
        } else {
            // If no OAuth params, re-check status if path changes, or on explicit trigger.
            // This is mostly covered by the initial checkStatus call.
        }
    }, [location.search, navigate, checkStatus]); // Ensure all relevant dependencies are here


     useEffect(() => {
        if (authResponse && authResponse.error) {
             console.error("Picker Auth Error:", authResponse);
             setError("Picker authentication failed. You might need to reconnect.");
        } else if (authResponse && authResponse.access_token){
             // console.log("Picker library got token:", authResponse.access_token)
        }
     }, [authResponse]);


    const handleConnect = async () => {
        setIsConnecting(true); // Show loading state for the button
        setError(''); // Clear previous errors
        const userAuthToken = localStorage.getItem("access_token"); // Your app's JWT

        try {
            const response = await axios.get(`${backendURL}/api/google-drive/auth/start`, {
                headers: { Authorization: `Bearer ${userAuthToken}` }
            });

            if (response.data && response.data.success && response.data.authorization_url) {
                // Success! Redirect the user to Google's OAuth page
                window.location.href = response.data.authorization_url;
            } else {
                // Backend indicated an error but didn't throw an HTTP error
                // (e.g., returned success:false from handle_controller_errors)
                console.error("Backend returned error for auth/start:", response.data);
                setError(response.data.message || "Failed to initiate Google connection. Please try again.");
            }
        } catch (err) {
            console.error("Error calling /auth/start:", err);
            if (err.response && err.response.data && err.response.data.message) {
                // Error from handle_controller_errors
                setError(err.response.data.message);
            } else {
                setError("Could not connect to Google Drive. Please check your network and try again.");
            }
        } finally {
            setIsConnecting(false); // Hide loading state for the button
        }
    };

    const handleDisconnect = async () => {
         setIsConnecting(true); // Use same loading state or a different one like isDisconnecting
         setError('');
         const token = localStorage.getItem("access_token");
         try {
             await axios.post(`${backendURL}/api/google-drive/disconnect`, {}, {
                 headers: { Authorization: `Bearer ${token}` }
             });
             setIsConnected(false);
             setAccessToken(null);
             setRefreshTrigger(prev => prev + 1);
             setError('Successfully disconnected from Google Drive.'); // Success message
         } catch (err) {
             console.error("Error disconnecting Google Drive:", err);
             setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
         } finally {
             setIsConnecting(false);
         }
     };

    const handleSync = async (fileIds) => {
        // ... (handleSync remains the same)
        if (!fileIds || fileIds.length === 0) {
            setError("No files selected to sync.");
            return;
        }
        setIsLoading(true); // Use main isLoading for sync as it affects the table
        setError('');
        const token = localStorage.getItem("access_token");
        try {
            const response = await axios.post(`${backendURL}/api/google-drive/sync`,
             { fileIds },
             { headers: { Authorization: `Bearer ${token}` } }
             );
            console.log("Sync response:", response.data);
            setError(response.data.message || 'Files synced successfully.');
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error("Error syncing Google Drive files:", err);
            setError(err.response?.data?.message || "Failed to sync files. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenPicker = () => {
        if (!accessToken) {
            setError("Access token missing. Cannot open picker. Please reconnect.");
            checkStatus(false); // Attempt to get token again without full load
            return;
        }
        if (!GOOGLE_API_KEY || !GOOGLE_CLIENT_ID) {
            setError("Missing Google API Key or Client ID configuration in the frontend.");
            console.error("Missing REACT_APP_GOOGLE_API_KEY or REACT_APP_GOOGLE_CLIENT_ID in .env");
            return;
        }
        openPicker({
            clientId: GOOGLE_CLIENT_ID,
            developerKey: GOOGLE_API_KEY,
            viewId: "DOCS", // Show all document types
            token: accessToken, // Pass the access token
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true, // Enable Shared Drives support
            multiselect: true, // Allow multiple file/folder selection
            callbackFunction: (data) => {
                if (data.action === "picked") {
                    const fileIds = data.docs.map((doc) => doc.id);
                    handleSync(fileIds); // Sync the selected files
                } else if (data.action === "cancel") {
                    console.log("Picker canceled by user");
                }
            },
        });
    };


    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"><strong>Google Drive Connector</strong></h1>
                    </div>

                    {isLoading && <p>Loading configuration...</p>}
                    
                    {/* Display error message below the button area */}
                    {error && (
                        <div className={`alert ${error.includes("Success") || error.includes("successfully") ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                            {error}
                        </div>
                    )}

                    {!isLoading && !isConnected && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>Connect your Google Drive account to select files for your chatbot.</p>
                                <button 
                                    className="btn btn-lg btn-primary" 
                                    onClick={handleConnect}
                                    disabled={isConnecting} // Disable button while connecting
                                >
                                    {isConnecting ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Connecting...</>
                                    ) : (
                                        <><i className="fa fa-google me-2"></i> Connect to Google Drive</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isLoading && isConnected && (
                         <div className="card">
                            <div className="card-header">
                                 <div className="row">
                                     <div className="col-lg-8">
                                        {/* <h5 className="card-title mb-0">Google Drive Integrations: </h5> */}
                                        <p>Please select the row(s) containing the desired files, then click the "Scrape and Train" button. Once the training is successfully completed, the "Trained" column will display "Yes". </p>
                                     </div>
                                     <div className="col-lg-4">
                                        <button className="btn btn-secondary me-2" onClick={handleOpenPicker} disabled={isLoading /* or another specific loading state */}>
                                            <i className="fa fa-plus me-1"></i> Select Files/Folders to Sync
                                        </button>
                                        <button className="btn btn-danger" onClick={handleDisconnect} disabled={isConnecting /* or another specific loading state */}>
                                            {isConnecting && !isConnected ? ( // Show spinner if disconnecting
                                                 <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Disconnecting...</>
                                            ) : (
                                                <><i className="fa fa-unlink me-1"></i> Disconnect</>
                                            )}
                                        </button>
                                    </div>
                                 </div>
                            </div>
                            <div className="card-body">
                                <ListGoogleDriveFiles refreshTrigger={refreshTrigger} />
                            </div>
                         </div>
                    )}
                </div>
            </main>
        </div>
    );
}